import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, Row, Col, Typography, Statistic, Table, Tag, Input, Select, Spin } from 'antd'
import {
  DollarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Breadcrumbs } from '../../components'
import { selectAllUsers } from '../../redux/features/user/userSelectors'

const { Title, Text } = Typography
const { Search } = Input

interface UserSubscription {
  key: string
  name: string
  email: string
  isSubscribed: boolean
  package: string
  amount: number
  subscriptionDate: string
}

const SubscriptionsPage: React.FC = () => {
  const users = useSelector(selectAllUsers) || []
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Transform user data for subscriptions
  const subscriptionData: UserSubscription[] = users.map((user: any) => ({
    key: user.userId || user._id || Math.random().toString(),
    name: user.name || 'Anonymous',
    email: user.email || 'N/A',
    isSubscribed: user.isSubscribed || false,
    package: user.package || 'None',
    amount: user.amount || 0,
    subscriptionDate: user.subscriptionStartDate
      ? new Date(user.subscriptionStartDate).toLocaleDateString()
      : 'N/A',
  }))

  // Filter data
  const filteredData = subscriptionData.filter(item => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.email.toLowerCase().includes(searchText.toLowerCase())

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'subscribed' && item.isSubscribed) ||
      (filterStatus === 'unsubscribed' && !item.isSubscribed)

    return matchesSearch && matchesFilter
  })

  // Calculate stats
  const subscribedCount = users.filter((u: any) => u.isSubscribed).length
  const unsubscribedCount = users.length - subscribedCount
  const totalRevenue = users.reduce((sum, u: any) => sum + (u.amount || 0), 0)
  const averageRevenue = subscribedCount > 0 ? totalRevenue / subscribedCount : 0

  // Package breakdown
  const packageBreakdown = users.reduce((acc: { [key: string]: number }, user: any) => {
    const pkg = user.package || 'None'
    acc[pkg] = (acc[pkg] || 0) + 1
    return acc
  }, {})

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: UserSubscription, b: UserSubscription) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Status',
      dataIndex: 'isSubscribed',
      key: 'isSubscribed',
      render: (isSubscribed: boolean) => (
        <Tag color={isSubscribed ? 'green' : 'default'} icon={isSubscribed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isSubscribed ? 'Subscribed' : 'Free'}
        </Tag>
      ),
      filters: [
        { text: 'Subscribed', value: true },
        { text: 'Free', value: false },
      ],
      onFilter: (value: boolean | React.Key, record: UserSubscription) => record.isSubscribed === value,
    },
    {
      title: 'Package',
      dataIndex: 'package',
      key: 'package',
      render: (pkg: string) => (
        <Tag color={pkg !== 'None' ? 'blue' : 'default'}>
          {pkg}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `$${amount.toFixed(2)}`,
      sorter: (a: UserSubscription, b: UserSubscription) => a.amount - b.amount,
    },
    {
      title: 'Subscription Date',
      dataIndex: 'subscriptionDate',
      key: 'subscriptionDate',
    },
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" tip="Loading subscriptions..." />
      </div>
    )
  }

  return (
    <div>
      <Breadcrumbs />
      <div style={{ padding: '20px' }}>
        <Title level={4}>Subscription Management</Title>

        {/* Stats Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Users"
                value={users.length}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Subscribed"
                value={subscribedCount}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Free Users"
                value={unsubscribedCount}
                valueStyle={{ color: '#999' }}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={totalRevenue}
                precision={2}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Package Breakdown */}
        <Title level={5}>Subscription Packages</Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {Object.entries(packageBreakdown).map(([pkg, count]) => (
            <Col xs={12} sm={8} md={6} key={pkg}>
              <Card size="small">
                <Statistic
                  title={pkg}
                  value={count as number}
                  suffix="users"
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Subscription Table */}
        <Title level={5}>All Subscriptions</Title>
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
            <Search
              placeholder="Search by name or email"
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="all"
              style={{ width: 150 }}
              onChange={(value) => setFilterStatus(value)}
              options={[
                { value: 'all', label: 'All Users' },
                { value: 'subscribed', label: 'Subscribed' },
                { value: 'unsubscribed', label: 'Free' },
              ]}
            />
          </div>
          <Table
            dataSource={filteredData}
            columns={columns}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        </Card>
      </div>
    </div>
  )
}

export default SubscriptionsPage
